import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { FilterState, defaultFilters } from '@/components/projects/ProjectFilters';

export type ProjectsViewMode = 'list' | 'command';

interface ProjectFilterContextType {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  isDrawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  clearFilters: () => void;
  activeFilterCount: number;
  viewMode: ProjectsViewMode;
  setViewMode: (mode: ProjectsViewMode) => void;
}

const ProjectFilterContext = createContext<ProjectFilterContextType | undefined>(undefined);

function readSession<T>(key: string, fallback: T): T {
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch { return fallback; }
}

// Read deep-link filter intent from URL on mount. Runs once per provider mount
// so dashboard tile clicks land in a pre-filtered Command Center.
function readUrlFilters(base: FilterState): FilterState {
  if (typeof window === 'undefined') return base;
  const params = new URLSearchParams(window.location.search);
  const next: FilterState = { ...base };
  const risk = params.get('riskLevel');
  if (risk === 'high' || risk === 'medium' || risk === 'low') next.riskLevel = risk;
  if (params.get('stalled') === 'true') next.stalled = true;
  if (params.get('lowQuality') === 'true') next.lowQuality = true;
  if (params.get('qualityDeclining') === 'true') next.qualityDeclining = true;
  return next;
}

function readUrlViewMode(base: ProjectsViewMode): ProjectsViewMode {
  if (typeof window === 'undefined') return base;
  const v = new URLSearchParams(window.location.search).get('view');
  if (v === 'command' || v === 'list') return v;
  return base;
}

export function ProjectFilterProvider({ children }: { children: ReactNode }) {
  const [searchQuery, setSearchQuery] = useState(() => readSession<string>('filters:projects:search', ''));
  const [filters, setFilters] = useState<FilterState>(() =>
    readUrlFilters(readSession<FilterState>('filters:projects:state', defaultFilters))
  );
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ProjectsViewMode>(() =>
    readUrlViewMode(readSession<ProjectsViewMode>('filters:projects:viewMode', 'list'))
  );

  useEffect(() => { sessionStorage.setItem('filters:projects:search', JSON.stringify(searchQuery)); }, [searchQuery]);
  useEffect(() => { sessionStorage.setItem('filters:projects:state', JSON.stringify(filters)); }, [filters]);
  useEffect(() => { sessionStorage.setItem('filters:projects:viewMode', JSON.stringify(viewMode)); }, [viewMode]);

  const clearFilters = () => {
    setSearchQuery('');
    setFilters(defaultFilters);
  };

  // Count only filters that are visible and clearable in the drawer UI.
  // Hidden deep-link flags (riskLevel, goNoGoVerdict, stalled, lowQuality,
  // qualityDeclining) intentionally excluded — otherwise the badge can show
  // with no controls to clear, confusing users.
  const activeFilterCount = [
    filters.status !== 'all',
    filters.client !== 'all',
    filters.owner !== 'all',
    filters.tag !== 'all',
    filters.dueDate !== 'all',
    filters.dealValueMin !== null,
    filters.dealValueMax !== null,
    searchQuery.trim() !== '',
  ].filter(Boolean).length;

  return (
    <ProjectFilterContext.Provider
      value={{
        searchQuery,
        setSearchQuery,
        filters,
        setFilters,
        isDrawerOpen,
        setDrawerOpen,
        clearFilters,
        activeFilterCount,
        viewMode,
        setViewMode,
      }}
    >
      {children}
    </ProjectFilterContext.Provider>
  );
}

export function useProjectFilters() {
  const context = useContext(ProjectFilterContext);
  if (!context) {
    throw new Error('useProjectFilters must be used within a ProjectFilterProvider');
  }
  return context;
}
